import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  CheckSquare,
  Square,
  Eye,
  Download,
  MoreVertical,
  Info,
  Loader2,
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  Search,
  Trash2,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import CreateVendorModal from './CreateVendorModal';

export interface EnhancedTableRow {
  id: string;
  row_number: number;
  [key: string]: any;
  status: 'success' | 'warning' | 'failure';
  statusReason?: string;
  missingFields?: string[];
  validationErrors?: Array<{ field: string; message: string }>;
  warnings?: Array<{ field: string; message: string }>;
  canProceed?: boolean;
}

export interface TableColumn {
  key: string;
  label: string;
  width?: string;
  editable?: boolean;
  required?: boolean;
  type?: 'text' | 'select' | 'number' | 'date';
  options?: Array<{ value: string; label: string }>;
  tooltip?: string;
}

interface EnhancedExcelTableProps {
  rows: EnhancedTableRow[];
  columns: TableColumn[];
  selectedRows: string[];
  onSelectionChange: (selected: string[]) => void;
  onCellEdit?: (rowId: string, field: string, value: any) => Promise<void>;
  onRowAction?: (rowId: string) => void;
  onVendorCreated?: (vendor: { id: string; name: string }) => void;
  onBulkDelete?: (rowIds: string[]) => Promise<void>;
  properties?: Array<{ code: string; name: string }>;
  vendors?: Array<{ id: string; name: string }>;
  commodities?: string[];
  showStatusColumn?: boolean;
  showActionsColumn?: boolean;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * EnhancedExcelTable - Full keyboard navigation Excel-style table
 * 
 * Fixes implemented:
 * - Fix #2: Pagination for 10,000+ rows
 * - Fix #3: Status filter TABS (not dropdown)
 * - Fix #4: Advanced filter toolbar
 * - Fix #5: Create new vendor functionality
 * - Fix #6: Improved cell editing with error handling
 */
export default function EnhancedExcelTable({
  rows,
  columns,
  selectedRows,
  onSelectionChange,
  onCellEdit,
  onRowAction,
  onVendorCreated,
  onBulkDelete,
  properties = [],
  vendors = [],
  commodities = [],
  showStatusColumn = true,
  showActionsColumn = true,
}: EnhancedExcelTableProps) {
  // Editing state
  const [editingCell, setEditingCell] = useState<{ rowId: string; field: string } | null>(null);
  const [focusedCell, setFocusedCell] = useState<{ rowIndex: number; colIndex: number } | null>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<string, { rowId: string; field: string; value: any }>>(new Map());
  const [recentlySaved, setRecentlySaved] = useState<Set<string>>(new Set());
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  
  // Column visibility - DEFAULT to essential columns only
  const defaultVisibleColumns = ['account_number', 'vendor_name', 'commodity_type', 'property_name', 'status'];
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    // Load from localStorage or use defaults
    const saved = localStorage.getItem('stage2-visible-columns');
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch {
        return new Set(defaultVisibleColumns);
      }
    }
    return new Set(defaultVisibleColumns);
  });
  
  // Persist column visibility to localStorage
  const updateVisibleColumns = (newVisible: Set<string>) => {
    setVisibleColumns(newVisible);
    localStorage.setItem('stage2-visible-columns', JSON.stringify([...newVisible]));
  };
  
  // Show All / Hide All handlers
  const showAllColumns = () => {
    const allKeys = columns.map(c => c.key);
    updateVisibleColumns(new Set(allKeys));
  };
  
  const hideAllColumns = () => {
    // Keep at least one column visible
    updateVisibleColumns(new Set(['account_number']));
  };
  
  // Fix #3: Status filter tabs - with localStorage persistence
  const [filterStatus, setFilterStatus] = useState<'all' | 'success' | 'warning' | 'failure'>(() => {
    const saved = localStorage.getItem('stage2-filter-status');
    if (saved && ['all', 'success', 'warning', 'failure'].includes(saved)) {
      return saved as 'all' | 'success' | 'warning' | 'failure';
    }
    return 'all';
  });
  
  // Fix #4: Advanced filters - with localStorage persistence
  const [searchTerm, setSearchTerm] = useState(() => {
    return localStorage.getItem('stage2-search-term') || '';
  });
  const [propertyFilter, setPropertyFilter] = useState<string>(() => {
    return localStorage.getItem('stage2-property-filter') || 'all';
  });
  const [vendorFilter, setVendorFilter] = useState<string>(() => {
    return localStorage.getItem('stage2-vendor-filter') || 'all';
  });
  const [commodityFilter, setCommodityFilter] = useState<string>(() => {
    return localStorage.getItem('stage2-commodity-filter') || 'all';
  });
  
  // Fix #2: Pagination - with localStorage persistence
  const [currentPage, setCurrentPage] = useState(() => {
    const saved = localStorage.getItem('stage2-current-page');
    return saved ? parseInt(saved, 10) || 1 : 1;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem('stage2-rows-per-page');
    return saved ? parseInt(saved, 10) || 100 : 100;
  });
  
  // Fix #5: Create vendor modal
  const [showCreateVendorModal, setShowCreateVendorModal] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  
  // Bulk delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const tableRef = useRef<HTMLTableElement>(null);

  // Status counts (before filtering)
  const statusCounts = useMemo(() => ({
    all: rows.length,
    success: rows.filter(r => r.status === 'success').length,
    warning: rows.filter(r => r.status === 'warning').length,
    failure: rows.filter(r => r.status === 'failure').length,
  }), [rows]);

  // Get unique values for filters
  const uniqueProperties = useMemo(() => {
    const props = new Set<string>();
    rows.forEach(r => {
      if (r.property_name) props.add(r.property_name);
    });
    return Array.from(props).sort();
  }, [rows]);

  const uniqueCommodities = useMemo(() => {
    const comms = new Set<string>();
    rows.forEach(r => {
      if (r.commodity_type) comms.add(r.commodity_type);
    });
    return Array.from(comms).sort();
  }, [rows]);

  const uniqueVendors = useMemo(() => {
    const vends = new Set<string>();
    rows.forEach(r => {
      if (r.vendor_name) vends.add(r.vendor_name);
    });
    return Array.from(vends).sort();
  }, [rows]);

  // Detect unmatched vendors (Fix #5)
  const unmatchedVendors = useMemo(() => {
    const vendorNames = new Set(vendors.map(v => v.name.toLowerCase()));
    return [...new Set(rows.filter(r => 
      r.vendor_name && !vendorNames.has(r.vendor_name.toLowerCase())
    ).map(r => r.vendor_name))];
  }, [rows, vendors]);

  // Check if any filters are active
  const hasActiveFilters = filterStatus !== 'all' || searchTerm || propertyFilter !== 'all' || vendorFilter !== 'all' || commodityFilter !== 'all';

  // Persist filter states to localStorage
  useEffect(() => {
    localStorage.setItem('stage2-filter-status', filterStatus);
  }, [filterStatus]);
  
  useEffect(() => {
    localStorage.setItem('stage2-search-term', searchTerm);
  }, [searchTerm]);
  
  useEffect(() => {
    localStorage.setItem('stage2-property-filter', propertyFilter);
  }, [propertyFilter]);
  
  useEffect(() => {
    localStorage.setItem('stage2-vendor-filter', vendorFilter);
  }, [vendorFilter]);
  
  useEffect(() => {
    localStorage.setItem('stage2-commodity-filter', commodityFilter);
  }, [commodityFilter]);
  
  useEffect(() => {
    localStorage.setItem('stage2-current-page', currentPage.toString());
  }, [currentPage]);
  
  useEffect(() => {
    localStorage.setItem('stage2-rows-per-page', rowsPerPage.toString());
  }, [rowsPerPage]);

  const clearAllFilters = () => {
    setFilterStatus('all');
    setSearchTerm('');
    setPropertyFilter('all');
    setVendorFilter('all');
    setCommodityFilter('all');
    setCurrentPage(1);
    // Also clear localStorage
    localStorage.removeItem('stage2-filter-status');
    localStorage.removeItem('stage2-search-term');
    localStorage.removeItem('stage2-property-filter');
    localStorage.removeItem('stage2-vendor-filter');
    localStorage.removeItem('stage2-commodity-filter');
    localStorage.removeItem('stage2-current-page');
  };

  // Filter rows (Fix #3 & #4)
  const filteredRows = useMemo(() => {
    let filtered = rows;
    
    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(row => row.status === filterStatus);
    }
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(row =>
        Object.values(row).some(val => String(val).toLowerCase().includes(term))
      );
    }
    
    // Property filter
    if (propertyFilter && propertyFilter !== 'all') {
      filtered = filtered.filter(row => row.property_name === propertyFilter);
    }
    
    // Vendor filter
    if (vendorFilter && vendorFilter !== 'all') {
      filtered = filtered.filter(row => row.vendor_name === vendorFilter);
    }
    
    // Commodity filter
    if (commodityFilter && commodityFilter !== 'all') {
      filtered = filtered.filter(row => row.commodity_type === commodityFilter);
    }
    
    return filtered;
  }, [rows, filterStatus, searchTerm, propertyFilter, vendorFilter, commodityFilter]);

  // Fix #2: Pagination calculations
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, filteredRows.length);
  const paginatedRows = filteredRows.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [filterStatus, searchTerm, propertyFilter, vendorFilter, commodityFilter]);

  const visibleColumnsList = columns.filter(c => visibleColumns.has(c.key));

  // Debounced auto-save with faster response (500ms instead of 2000ms)
  const debouncedSave = useDebouncedCallback(async () => {
    if (pendingChanges.size === 0) return;
    
    setSaveStatus('saving');
    const changesToSave = Array.from(pendingChanges.values());
    
    try {
      for (const change of changesToSave) {
        if (onCellEdit) {
          await onCellEdit(change.rowId, change.field, change.value);
        }
      }
      
      const savedKeys = new Set(changesToSave.map(c => `${c.rowId}-${c.field}`));
      setRecentlySaved(savedKeys);
      setPendingChanges(new Map());
      setSaveStatus('saved');
      
      setTimeout(() => {
        setRecentlySaved(new Set());
        setSaveStatus('idle');
      }, 2000);
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, 500);

  // Handle cell value change
  const handleCellChange = (rowId: string, field: string, value: any) => {
    const key = `${rowId}-${field}`;
    setPendingChanges(prev => {
      const next = new Map(prev);
      next.set(key, { rowId, field, value });
      return next;
    });
    debouncedSave();
  };

  // Immediate save (on blur/enter) - Fix #6
  const handleCellSave = async (rowId: string, field: string, value: any) => {
    if (onCellEdit) {
      try {
        setSaveStatus('saving');
        await onCellEdit(rowId, field, value);
        const key = `${rowId}-${field}`;
        setRecentlySaved(new Set([key]));
        setSaveStatus('saved');
        setTimeout(() => {
          setRecentlySaved(new Set());
          setSaveStatus('idle');
        }, 2000);
      } catch (error) {
        console.error('Save failed:', error);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    }
    setEditingCell(null);
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, rowIndex: number, colIndex: number) => {
    const totalCols = visibleColumnsList.length;
    const totalRows = paginatedRows.length;

    switch (e.key) {
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          if (colIndex > 0) {
            setFocusedCell({ rowIndex, colIndex: colIndex - 1 });
          } else if (rowIndex > 0) {
            setFocusedCell({ rowIndex: rowIndex - 1, colIndex: totalCols - 1 });
          }
        } else {
          if (colIndex < totalCols - 1) {
            setFocusedCell({ rowIndex, colIndex: colIndex + 1 });
          } else if (rowIndex < totalRows - 1) {
            setFocusedCell({ rowIndex: rowIndex + 1, colIndex: 0 });
          }
        }
        break;
      case 'Enter':
        if (editingCell) {
          e.preventDefault();
          setFocusedCell({ rowIndex: Math.min(rowIndex + 1, totalRows - 1), colIndex });
        } else {
          const column = visibleColumnsList[colIndex];
          if (column?.editable !== false) {
            setEditingCell({ rowId: paginatedRows[rowIndex].id, field: column.key });
          }
        }
        break;
      case 'Escape':
        setEditingCell(null);
        break;
      case 'ArrowUp':
        if (!editingCell) {
          e.preventDefault();
          setFocusedCell({ rowIndex: Math.max(0, rowIndex - 1), colIndex });
        }
        break;
      case 'ArrowDown':
        if (!editingCell) {
          e.preventDefault();
          setFocusedCell({ rowIndex: Math.min(totalRows - 1, rowIndex + 1), colIndex });
        }
        break;
      case 'ArrowLeft':
        if (!editingCell) {
          e.preventDefault();
          setFocusedCell({ rowIndex, colIndex: Math.max(0, colIndex - 1) });
        }
        break;
      case 'ArrowRight':
        if (!editingCell) {
          e.preventDefault();
          setFocusedCell({ rowIndex, colIndex: Math.min(totalCols - 1, colIndex + 1) });
        }
        break;
    }
  };

  const handleSelectAll = () => {
    if (selectedRows.length === paginatedRows.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(paginatedRows.map(r => r.id));
    }
  };

  const handleSelectAllFiltered = () => {
    onSelectionChange(filteredRows.map(r => r.id));
  };

  const handleRowSelect = (rowId: string) => {
    const newSelected = selectedRows.includes(rowId)
      ? selectedRows.filter(id => id !== rowId)
      : [...selectedRows, rowId];
    onSelectionChange(newSelected);
  };

  const handleBulkDeleteClick = () => {
    if (selectedRows.length > 0) {
      setShowDeleteConfirm(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!onBulkDelete || selectedRows.length === 0) return;
    
    setIsDeleting(true);
    try {
      await onBulkDelete(selectedRows);
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusBadge = (row: EnhancedTableRow) => {
    switch (row.status) {
      case 'success':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Success
          </Badge>
        );
      case 'warning':
        return (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Warning
          </Badge>
        );
      case 'failure':
        return (
          <Badge variant="destructive">
            <AlertCircle className="h-3 w-3 mr-1" />
            Error
          </Badge>
        );
    }
  };

  const isFieldMissing = (row: EnhancedTableRow, field: string) => {
    return row.missingFields?.includes(field) || row.validationErrors?.some(e => e.field === field);
  };

  const getCellClassName = (row: EnhancedTableRow, column: TableColumn, rowIndex: number, colIndex: number) => {
    const key = `${row.id}-${column.key}`;
    const isPending = pendingChanges.has(key);
    const isRecentlySaved = recentlySaved.has(key);
    const isFocused = focusedCell?.rowIndex === rowIndex && focusedCell?.colIndex === colIndex;
    const isEditing = editingCell?.rowId === row.id && editingCell?.field === column.key;
    const isMissing = isFieldMissing(row, column.key);

    return cn(
      'transition-all duration-200',
      isFocused && !isEditing && 'ring-2 ring-primary ring-inset',
      isPending && 'bg-yellow-100',
      isRecentlySaved && 'bg-green-100 animate-pulse',
      isMissing && 'bg-red-50'
    );
  };

  // Check if vendor is unmatched (Fix #5)
  const isUnmatchedVendor = (vendorName: string) => {
    if (!vendorName) return false;
    return !vendors.find(v => v.name.toLowerCase() === vendorName.toLowerCase());
  };

  const renderCell = (row: EnhancedTableRow, column: TableColumn, rowIndex: number, colIndex: number) => {
    const isEditing = editingCell?.rowId === row.id && editingCell?.field === column.key;
    const isMissing = isFieldMissing(row, column.key);
    const value = row[column.key] ?? '';

    if (isEditing) {
      if (column.type === 'select' || column.key === 'commodity_type' || column.key === 'vendor_name') {
        let options: Array<{ value: string; label: string }> = [];
        
        if (column.key === 'commodity_type') {
          options = ['Electricity', 'Water', 'Gas', 'Sewer', 'Trash', 'Storm Drain', 'Irrigation']
            .map(c => ({ value: c, label: c }));
          commodities.forEach(c => {
            if (!options.find(o => o.value === c)) {
              options.push({ value: c, label: c });
            }
          });
        } else if (column.key === 'vendor_name') {
          options = vendors.map(v => ({ value: v.name, label: v.name }));
        } else if (column.options) {
          options = column.options;
        }

        return (
          <Select
            defaultValue={String(value)}
            onValueChange={(val) => handleCellSave(row.id, column.key, val)}
            onOpenChange={(open) => !open && setEditingCell(null)}
          >
            <SelectTrigger className="h-8 focus:ring-2 focus:ring-primary">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }

      return (
        <Input
          defaultValue={String(value)}
          onBlur={(e) => handleCellSave(row.id, column.key, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleCellSave(row.id, column.key, e.currentTarget.value);
              handleKeyDown(e, rowIndex, colIndex);
            } else if (e.key === 'Escape') {
              setEditingCell(null);
            } else if (e.key === 'Tab') {
              e.preventDefault();
              handleCellSave(row.id, column.key, e.currentTarget.value);
              handleKeyDown(e, rowIndex, colIndex);
            }
          }}
          autoFocus
          className="h-8 focus:ring-2 focus:ring-primary"
        />
      );
    }

    // Fix #5: Show create vendor button for unmatched vendors
    if (column.key === 'vendor_name' && value && isUnmatchedVendor(value)) {
      return (
        <div className="flex items-center gap-2">
          <span className="text-destructive font-medium">{value}</span>
          <Badge variant="destructive" className="text-xs shrink-0">Not Found</Badge>
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-6 px-2 text-xs shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              setNewVendorName(value);
              setShowCreateVendorModal(true);
            }}
          >
            <Plus className="h-3 w-3 mr-1" />
            Create
          </Button>
        </div>
      );
    }

    return (
      <div
        tabIndex={0}
        onClick={() => {
          if (column.editable !== false) {
            setEditingCell({ rowId: row.id, field: column.key });
          }
        }}
        onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
        onFocus={() => setFocusedCell({ rowIndex, colIndex })}
        className={cn(
          'cursor-pointer px-2 py-1 rounded min-h-[28px] outline-none',
          isMissing && 'bg-red-100 text-destructive font-medium',
          !isMissing && !value && column.required && 'bg-red-50 text-destructive italic'
        )}
        title={isMissing ? `Missing required field: ${column.label}` : 'Click to edit'}
      >
        {value || (column.required ? '[Required]' : '-')}
      </div>
    );
  };

  const getRowClassName = (row: EnhancedTableRow) => {
    switch (row.status) {
      case 'success':
        return 'bg-green-50/50 hover:bg-green-100/50 border-l-4 border-l-green-500';
      case 'warning':
        return 'bg-amber-50/50 hover:bg-amber-100/50 border-l-4 border-l-amber-500';
      case 'failure':
        return 'bg-red-50/50 hover:bg-red-100/50 border-l-4 border-l-red-500';
      default:
        return '';
    }
  };

  const handleVendorCreated = (vendor: { id: string; name: string }) => {
    onVendorCreated?.(vendor);
  };

  return (
    <Card className="border-0 shadow-none">
      <CardHeader className="py-1.5 px-3">
        <div className="flex flex-col gap-1.5">
          {/* Bulk Actions Bar - Show when rows selected */}
          {selectedRows.length > 0 && (
            <div className="flex items-center justify-between px-2 py-1 bg-primary/10 rounded border border-primary/20">
              <span className="text-xs font-medium">{selectedRows.length} row(s) selected</span>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-7 text-xs"
                  onClick={handleSelectAllFiltered}
                >
                  Select All ({filteredRows.length})
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-7 text-xs"
                  onClick={() => onSelectionChange([])}
                >
                  Clear
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  className="h-7 text-xs"
                  onClick={handleBulkDeleteClick}
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Delete Selected
                </Button>
              </div>
            </div>
          )}

          {/* Title and save status - ultra compact */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-xs font-medium">{filteredRows.length.toLocaleString()} rows</CardTitle>
              {saveStatus === 'saving' && (
                <span className="text-[10px] text-blue-600 inline-flex items-center gap-0.5">
                  <Loader2 className="h-2.5 w-2.5 animate-spin" /> Saving
                </span>
              )}
              {saveStatus === 'saved' && (
                <span className="text-[10px] text-green-600 inline-flex items-center gap-0.5">
                  <CheckCircle2 className="h-2.5 w-2.5" /> Saved
                </span>
              )}
              {saveStatus === 'error' && (
                <span className="text-[10px] text-destructive inline-flex items-center gap-0.5">
                  <AlertCircle className="h-2.5 w-2.5" /> Failed
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-xs">
                    <Eye className="h-3 w-3 mr-1" />
                    Columns ({visibleColumns.size}/{columns.length})
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="text-xs flex items-center justify-between">
                    <span>Toggle Columns</span>
                    <span className="text-[10px] text-muted-foreground">
                      {visibleColumns.size}/{columns.length} visible
                    </span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  
                  {/* Show All / Hide All quick actions */}
                  <div className="flex gap-1 px-2 py-1">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-6 text-[10px] flex-1"
                      onClick={showAllColumns}
                    >
                      Show All
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-6 text-[10px] flex-1"
                      onClick={hideAllColumns}
                    >
                      Hide All
                    </Button>
                  </div>
                  
                  <DropdownMenuSeparator />
                  {columns.map(column => (
                    <DropdownMenuCheckboxItem
                      key={column.key}
                      checked={visibleColumns.has(column.key)}
                      className="text-xs"
                      onCheckedChange={(checked) => {
                        const newVisible = new Set(visibleColumns);
                        if (checked) {
                          newVisible.add(column.key);
                        } else {
                          // Don't allow hiding all columns
                          if (newVisible.size > 1) {
                            newVisible.delete(column.key);
                          }
                        }
                        updateVisibleColumns(newVisible);
                      }}
                    >
                      <span className="flex items-center gap-1">
                        {column.label}
                        {column.required && (
                          <span className="text-destructive text-[10px]">*</span>
                        )}
                      </span>
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Button variant="outline" size="sm" className="h-7 text-xs">
                <Download className="h-3 w-3 mr-1" />
                Export
              </Button>
            </div>
          </div>

          {/* Fix #3: Status Filter TABS - ultra compact */}
          <div className="flex items-center border-b text-[10px]">
            <button 
              onClick={() => setFilterStatus('all')}
              className={cn(
                "px-2 py-1 font-medium border-b-2 transition-colors",
                filterStatus === 'all' 
                  ? "border-primary text-primary" 
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              All ({statusCounts.all.toLocaleString()})
            </button>
            <button 
              onClick={() => setFilterStatus('success')}
              className={cn(
                "px-2 py-1 font-medium border-b-2 transition-colors flex items-center gap-0.5",
                filterStatus === 'success' 
                  ? "border-green-600 text-green-700" 
                  : "text-muted-foreground hover:text-foreground border-transparent"
              )}
            >
              <CheckCircle2 className="h-2.5 w-2.5" />
              OK ({statusCounts.success.toLocaleString()})
            </button>
            <button 
              onClick={() => setFilterStatus('failure')}
              className={cn(
                "px-2 py-1 font-medium border-b-2 transition-colors flex items-center gap-0.5",
                filterStatus === 'failure' 
                  ? "border-red-600 text-red-700" 
                  : "text-muted-foreground hover:text-foreground border-transparent"
              )}
            >
              <AlertCircle className="h-2.5 w-2.5" />
              Err ({statusCounts.failure.toLocaleString()})
            </button>
            <button 
              onClick={() => setFilterStatus('warning')}
              className={cn(
                "px-2 py-1 font-medium border-b-2 transition-colors flex items-center gap-0.5",
                filterStatus === 'warning' 
                  ? "border-amber-600 text-amber-700" 
                  : "text-muted-foreground hover:text-foreground border-transparent"
              )}
            >
              <AlertTriangle className="h-2.5 w-2.5" />
              Warn ({statusCounts.warning.toLocaleString()})
            </button>
          </div>

          {/* Fix #4: Advanced Filter Toolbar - ultra compact */}
          <div className="flex flex-wrap items-center gap-1 p-1.5 bg-muted/20 rounded text-[10px]">
            <div className="relative">
              <Search className="absolute left-1.5 top-1 h-2.5 w-2.5 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-24 pl-5 h-5 text-[10px]"
              />
            </div>
            
            <Select value={propertyFilter} onValueChange={setPropertyFilter}>
              <SelectTrigger className="w-24 h-5 text-[10px]">
                <SelectValue placeholder="Property" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Props</SelectItem>
                {uniqueProperties.map(p => (
                  <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={commodityFilter} onValueChange={setCommodityFilter}>
              <SelectTrigger className="w-24 h-5 text-[10px]">
                <SelectValue placeholder="Commodity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Comm</SelectItem>
                {uniqueCommodities.map(c => (
                  <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={vendorFilter} onValueChange={setVendorFilter}>
              <SelectTrigger className="w-24 h-5 text-[10px]">
                <SelectValue placeholder="Vendor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All Vend</SelectItem>
                {uniqueVendors.map(v => (
                  <SelectItem key={v} value={v} className="text-xs">{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-5 text-[10px] px-1.5 ml-auto" onClick={clearAllFilters}>
                <X className="h-2.5 w-2.5 mr-0.5" />
                Clear
              </Button>
            )}
          </div>

          {/* Active filter badges - compact */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-1 text-[10px]">
              <span className="text-muted-foreground">Active:</span>
              {filterStatus !== 'all' && (
                <Badge variant="secondary" className="gap-0.5 h-4 px-1 text-[10px]">
                  {filterStatus}
                  <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setFilterStatus('all')} />
                </Badge>
              )}
              {searchTerm && (
                <Badge variant="secondary" className="gap-0.5 h-4 px-1 text-[10px]">
                  "{searchTerm}"
                  <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setSearchTerm('')} />
                </Badge>
              )}
              {propertyFilter !== 'all' && (
                <Badge variant="secondary" className="gap-0.5 h-4 px-1 text-[10px]">
                  {propertyFilter}
                  <X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setPropertyFilter('all')} />
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {/* Table Container - horizontal scroll ONLY within this container */}
        <div className="w-full overflow-x-auto max-h-[calc(100vh-380px)]">
          <Table ref={tableRef} className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 sticky left-0 bg-card z-10">
                  <button onClick={handleSelectAll} className="cursor-pointer">
                    {selectedRows.length === paginatedRows.length && paginatedRows.length > 0 ? (
                      <CheckSquare className="h-4 w-4" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>
                </TableHead>
                <TableHead className="w-16">#</TableHead>
                {showStatusColumn && <TableHead className="w-28">Status</TableHead>}
                {visibleColumnsList.map(column => (
                  <TableHead key={column.key} style={{ width: column.width, minWidth: '120px' }}>
                    <TooltipProvider>
                      <div className="flex items-center gap-1">
                        {column.label}
                        {column.required && <span className="text-destructive">*</span>}
                        {column.tooltip && (
                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="h-3 w-3 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>{column.tooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TooltipProvider>
                  </TableHead>
                ))}
                {showActionsColumn && <TableHead className="w-16">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnsList.length + (showStatusColumn ? 4 : 3)} className="text-center py-12 text-muted-foreground">
                    {hasActiveFilters ? 'No matching data found' : 'No data to display'}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((row, rowIndex) => {
                  const isSelected = selectedRows.includes(row.id);

                  return (
                    <TableRow
                      key={row.id}
                      className={cn(
                        getRowClassName(row),
                        isSelected && 'ring-2 ring-primary'
                      )}
                    >
                      <TableCell className="sticky left-0 bg-inherit z-10" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => handleRowSelect(row.id)} className="cursor-pointer">
                          {isSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                        </button>
                      </TableCell>
                      <TableCell className="font-medium text-muted-foreground">{row.row_number}</TableCell>
                      {showStatusColumn && (
                        <TableCell>
                          {getStatusBadge(row)}
                        </TableCell>
                      )}
                      {visibleColumnsList.map((column, colIndex) => (
                        <TableCell
                          key={column.key}
                          className={getCellClassName(row, column, rowIndex, colIndex)}
                        >
                          {renderCell(row, column, rowIndex, colIndex)}
                        </TableCell>
                      ))}
                      {showActionsColumn && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onRowAction?.(row.id)}
                            className="h-8 w-8 p-0"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Fix #2: Pagination Controls - more compact */}
        <div className="mt-2 flex flex-col sm:flex-row items-center justify-between gap-2 py-2 border-t text-xs">
          <div className="text-muted-foreground">
            Showing {startIndex + 1}-{endIndex} of {filteredRows.length.toLocaleString()}
            {filteredRows.length !== rows.length && (
              <span className="ml-1">(filtered from {rows.length.toLocaleString()})</span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Per page:</span>
            <Select 
              value={String(rowsPerPage)} 
              onValueChange={(v) => {
                setRowsPerPage(Number(v));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-16 h-6 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50" className="text-xs">50</SelectItem>
                <SelectItem value="100" className="text-xs">100</SelectItem>
                <SelectItem value="500" className="text-xs">500</SelectItem>
                <SelectItem value="1000" className="text-xs">1000</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-0.5 ml-2">
              <Button 
                variant="outline" 
                size="icon" 
                className="h-6 w-6"
                onClick={() => setCurrentPage(1)} 
                disabled={currentPage === 1}
              >
                <ChevronsLeft className="h-3 w-3" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-6 w-6"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-3 w-3" />
              </Button>
              
              <span className="px-2">
                {currentPage}/{totalPages || 1}
              </span>
              
              <Button 
                variant="outline" 
                size="icon" 
                className="h-6 w-6"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                disabled={currentPage >= totalPages}
              >
                <ChevronRight className="h-3 w-3" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-6 w-6"
                onClick={() => setCurrentPage(totalPages)} 
                disabled={currentPage >= totalPages}
              >
                <ChevronsRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>

        {/* Compact Legend - inline with pagination */}
        <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="font-medium">Legend:</span>
          <div className="flex items-center gap-0.5">
            <CheckCircle2 className="h-3 w-3 text-green-600" />
            <span>OK</span>
          </div>
          <div className="flex items-center gap-0.5">
            <AlertTriangle className="h-3 w-3 text-amber-600" />
            <span>Warn</span>
          </div>
          <div className="flex items-center gap-0.5">
            <AlertCircle className="h-3 w-3 text-destructive" />
            <span>Error</span>
          </div>
        </div>
      </CardContent>

      {/* Fix #5: Create Vendor Modal */}
      <CreateVendorModal
        isOpen={showCreateVendorModal}
        onClose={() => {
          setShowCreateVendorModal(false);
          setNewVendorName('');
        }}
        vendorName={newVendorName}
        existingVendors={vendors}
        onVendorCreated={handleVendorCreated}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedRows.length} row(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The selected rows will be permanently removed from the import data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
